import { PaymentSnapshotBalances } from '@willow/types-iso';

import { add } from '../utils/math';

export interface WriteoffAmounts {
  escrow: number;
  suspense: number;
  principal: number;
  interest?: number;
  corporateAdvances?: number;
  fees?: number;
}

type WriteoffAccount = 'principal' | 'interest' | 'suspense' | 'escrow' | 'corporateAdvances' | 'fees';

interface Problem {
  message: string;
  account: WriteoffAccount;
}

type Balances = Pick<
  PaymentSnapshotBalances,
  'escrowBalance' | 'statementSuspenseBalance' | 'futureStatementSuspenseBalance'
> & {
  outstandingBalance?: number;
  interestOutstanding?: number;
  feesOutstanding?: number;
  advancesPrincipalOutstanding?: number;
};

/**
 * Validates the amounts of a writeoff.
 *
 * @param currentBalances The current balances of the loan.
 * @param toWriteoff The amounts to write off.
 * @returns A list of problems with the writeoff. If empty, the writeoff is valid.
 */
export function validateWriteoffAmounts(currentBalances: Balances, toWriteoff: WriteoffAmounts): Problem[] {
  const problems: Problem[] = [];

  const interest = toWriteoff.interest ?? 0;
  const corporateAdvances = toWriteoff.corporateAdvances ?? 0;
  const fees = toWriteoff.fees ?? 0;

  const negativeAccounts: [WriteoffAccount, number][] = [
    ['principal', toWriteoff.principal],
    ['interest', interest],
    ['suspense', toWriteoff.suspense],
    ['escrow', toWriteoff.escrow],
    ['corporateAdvances', corporateAdvances],
    ['fees', fees],
  ];

  for (const [account, amount] of negativeAccounts) {
    if (amount < 0) {
      problems.push({ message: 'Write off amount cannot be negative', account });
    }
  }

  const total = add(toWriteoff.principal, interest, toWriteoff.suspense, toWriteoff.escrow, corporateAdvances, fees);
  if (total === 0) {
    for (const [account] of negativeAccounts) {
      problems.push({ message: 'Total amount must be greater than 0', account });
    }
  }

  if (toWriteoff.principal > 0) {
    const outstandingBalance = currentBalances.outstandingBalance ?? 0;
    if (outstandingBalance <= 0) {
      problems.push({
        message: 'Cannot write off non-positive principal balance',
        account: 'principal',
      });
    } else if (toWriteoff.principal > outstandingBalance) {
      problems.push({
        message: 'Cannot write off more than the outstanding principal balance',
        account: 'principal',
      });
    }
  }

  if (interest > 0) {
    const interestOutstanding = currentBalances.interestOutstanding ?? 0;
    if (interestOutstanding <= 0) {
      problems.push({ message: 'Cannot write off non-positive interest balance', account: 'interest' });
    } else if (interest > interestOutstanding) {
      problems.push({ message: 'Cannot write off more than the outstanding interest balance', account: 'interest' });
    }
  }

  if (toWriteoff.suspense > 0) {
    // TODO: enhance this to allow us to write off other suspense balances
    const statementSuspenseTotal = currentBalances.statementSuspenseBalance;
    if (statementSuspenseTotal >= 0) {
      problems.push({
        message: 'Cannot write off non-negative suspense balance',
        account: 'suspense',
      });
    } else if (add(toWriteoff.suspense, statementSuspenseTotal) > 0) {
      problems.push({
        message: 'Cannot write off more than the suspense negative balance',
        account: 'suspense',
      });
    }
  }

  if (toWriteoff.escrow > 0) {
    if (currentBalances.escrowBalance >= 0) {
      problems.push({
        message: 'Cannot write off non-negative escrow balance',
        account: 'escrow',
      });
    } else if (add(toWriteoff.escrow, currentBalances.escrowBalance) > 0) {
      problems.push({
        message: 'Cannot write off more than the escrow negative balance',
        account: 'escrow',
      });
    }
  }

  if (corporateAdvances > 0) {
    const advancesPrincipalOutstanding = currentBalances.advancesPrincipalOutstanding ?? 0;
    if (advancesPrincipalOutstanding <= 0) {
      problems.push({
        message: 'Cannot write off non-positive corporate advances balance',
        account: 'corporateAdvances',
      });
    } else if (corporateAdvances > advancesPrincipalOutstanding) {
      problems.push({
        message: 'Cannot write off more than the outstanding corporate advances balance',
        account: 'corporateAdvances',
      });
    }
  }

  if (fees > 0) {
    const feesOutstanding = currentBalances.feesOutstanding ?? 0;
    if (feesOutstanding <= 0) {
      problems.push({ message: 'Cannot write off non-positive fees balance', account: 'fees' });
    } else if (fees > feesOutstanding) {
      problems.push({ message: 'Cannot write off more than the outstanding fees balance', account: 'fees' });
    }
  }

  return problems;
}
